// controllers/sortable_html_controller.js
//
// A drag-to-reorder controller that sends a PATCH request on drop and replaces
// a target element with the HTML partial returned by the server.
//
// Usage:
//   data-controller="sortable-html"
//   data-sortable-html-resource-name-value="my_resource"   # param namespace  (required)
//   data-sortable-html-param-name-value="position"         # position param   (default: "position")
//   data-sortable-html-update-selector-value="#my-target"  # CSS selector for the element
//                                                          # whose innerHTML is replaced.
//                                                          # Walks up from the sortable element
//                                                          # then falls back to document.querySelector.
//                                                          # Omit to auto-detect [data-sortable-html-target="output"].
//
// Each sortable child item must carry:
//   data-sortable-update-url="<url>"   # PATCH endpoint for that item
//
import { Controller } from '@hotwired/stimulus'
import Sortable from 'sortablejs'

export default class extends Controller {
  static targets = ['output']
  static values = {
    handle: String,
    animation: { type: Number, default: 150 },
    ghostClass: { type: String, default: 'sortable-ghost' },
    dragClass: { type: String, default: 'sortable-drag' },
    chosenClass: { type: String, default: 'sortable-chosen' },
    selectedClass: { type: String, default: 'sortable-selected' },
    resourceName: String,
    paramName: { type: String, default: 'position' },
    updateSelector: String
  }

  connect() {
    this.sortable = Sortable.create(this.element, {
      animation: this.animationValue,
      handle: this.handleValue || undefined,
      ghostClass: this.ghostClassValue,
      dragClass: this.dragClassValue,
      chosenClass: this.chosenClassValue,
      selectedClass: this.selectedClassValue,
      onEnd: this.onEnd.bind(this)
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  onEnd(event) {
    const url = event.item.dataset.sortableUpdateUrl
    if (!url) {
      console.error('[sortable-html] No data-sortable-update-url on dragged item')
      return
    }

    const resourceName = this.resourceNameValue || 'resource'
    const body = {}
    body[resourceName] = { [this.paramNameValue]: event.newIndex + 1 }

    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content')

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        Accept: 'text/html, application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`[sortable-html] Server error: ${response.status}`)
        const contentType = response.headers.get('content-type')
        if (contentType?.includes('text/html')) {
          return response.text().then((html) => ({ html }))
        }
        return {}
      })
      .then((data) => {
        if (data.html) this.resolveOutputTarget().innerHTML = data.html
      })
      .catch((error) => {
        console.error('[sortable-html] Error updating position:', error)
      })
  }

  // Resolve the element whose innerHTML will be replaced with the server response.
  // Priority:
  //   1. data-sortable-html-output-target on a child element
  //   2. data-sortable-html-update-selector-value (CSS selector, walks up then searches doc)
  //   3. Closest ancestor matching [data-ajax-form-target="partialToUpdate"]
  //   4. Direct parent element
  resolveOutputTarget() {
    if (this.hasOutputTarget) return this.outputTarget

    if (this.hasUpdateSelectorValue) {
      return (
        this.element.closest(this.updateSelectorValue) ||
        document.querySelector(this.updateSelectorValue)
      )
    }

    return (
      this.element.closest('[data-ajax-form-target="partialToUpdate"]') ||
      this.element.parentElement
    )
  }
}
